import React, { useCallback, useEffect, useRef, useState } from 'react';
import PlayerPauseFilled from '@uber/base-icons/player-pause-filled';
import PlayerPlayFilled from '@uber/base-icons/player-play-filled';
import { Button, SHAPE, SIZE } from 'baseui/button';
import { Slider } from 'baseui/slider';
import { getCurrentFrame, getTimeFromInteger } from '../utils/utils';
import { Controls, Playtime, SliderWrapper, TimelineContainer } from './styled-components';
const sliderStyleOverrides = {
  Thumb: {
    style: {
      width: '15px',
      height: '15px'
    }
  },
  Track: {
    style: {
      paddingLeft: '0px',
      paddingRight: '0px',
      paddingBottom: '12px'
    }
  }
};
type TimelinePropsT = {
  scene: any;
  fps: number;
};
const Timeline = ({
  scene,
  fps
}: TimelinePropsT) => {
  const animation = scene.animationGroups[0];
  const [progress, setProgress] = useState([0]);
  const [isPlaying, setIsPlaying] = useState(true);
  const onBeforeRenderObservableRef = useRef(null);
  const onAnimationLoopObservableRef = useRef(null);
  const previousFrameRef = useRef(null);
  const addListeners = useCallback(() => {
    onBeforeRenderObservableRef.current = scene.onBeforeRenderObservable.add(() => {
      // this will execute on every frame change
      const currentFrame = getCurrentFrame(animation);
      if (previousFrameRef.current !== currentFrame) {
        previousFrameRef.current = currentFrame;
        setProgress([currentFrame]);
      }
    });
    onAnimationLoopObservableRef.current = animation.onAnimationLoopObservable.add(() => {
      animation.stop();
      animation.goToFrame(0);
      animation.pause();
      setIsPlaying(false);
    });
    return () => {
      scene.onBeforeRenderObservable.remove(onBeforeRenderObservableRef.current);
      animation.onAnimationLoopObservable.remove(onAnimationLoopObservableRef.current);
    };
  }, [animation, scene]);
  useEffect(() => {
    if (!animation) return;
    const removeListeners = addListeners();
    return () => {
      removeListeners();
    };
  }, [scene, animation, addListeners]);
  const onChange = (value: number) => {
    setProgress([value]);
    if (!animation.isPlaying) {
      animation.play(true);
      animation.goToFrame(value);
      animation.pause();
    } else {
      animation.goToFrame(value);
    }
  };
  const togglePlay = () => {
    setIsPlaying(isPlaying => !isPlaying);
    isPlaying ? animation.pause() : animation.play(true);
  };
  if (!animation) return null;
  const end = Number((animation.to / fps).toFixed(1));
  if (isNaN(end)) return null;
  const endTime = `${getTimeFromInteger(end)}`;
  const currentFrame = getCurrentFrame(animation) / fps;
  const currentTime = Number(currentFrame.toFixed(1));
  const {
    from: min = 0,
    to: max = 0
  } = animation;
  const shouldRenderTimeline = max > min; // slider expects min to be greater than max

  if (!shouldRenderTimeline) return null;
  return <TimelineContainer>
      <Controls $placement="left">
        <Button onClick={togglePlay} shape={SHAPE.circle} size={SIZE.compact}>
          {isPlaying ? <PlayerPauseFilled size={15} title="Pause" /> : <PlayerPlayFilled size={15} title="Play" />}
        </Button>
      </Controls>
      <SliderWrapper>
        <Slider value={progress} min={min} max={max}
      // @ts-expect-error todo(ts-migration) TS2345 Argument of type 'number | undefined' is not assignable to parameter of type 'number'.
      onChange={({
        value
      }) => value && onChange(value[0])} valueToLabel={value => `${(value / fps).toFixed(1)}s`} overrides={{
        ...sliderStyleOverrides,
        InnerTrack: {
          style: ({
            $theme
          }) => ({
            backgroundColor: $theme.colors.primary700
          })
        },
        TickBar: () => null
      }} />
      </SliderWrapper>
      <Controls $width="20%" $placement="right">
        <Playtime>
          {getTimeFromInteger(currentTime)} / {endTime}
        </Playtime>
      </Controls>
    </TimelineContainer>;
};
export default Timeline;